import React, {useEffect, useId, useState} from 'react';
import {InformationCircleIcon} from '@heroicons/react/24/outline';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import WyscoutAnalyticsDashboard from '@/components/players/WyscoutAnalyticsDashboard';
import {playerKeys} from '@/features/players/queries';
import {
  WyscoutImportAggregates,
  WyscoutImportFailure,
  WyscoutImportMonths,
  WyscoutImportRun,
  wyscoutImportAPI,
} from '@/features/players/wyscoutImportApi';
import {getV2Error} from '@/services/api/v2';
import LoadingStatus from '@/components/ui/LoadingStatus';
import Button from '@/components/ui/Button';
import ConfirmModal from '@/components/ui/ConfirmModal';
import WyscoutStatsSection from './WyscoutStatsSection';

interface Props {
  workspaceId: string;
  playerId: string;
  playerName: string;
  position: string;
  canImport: boolean;
  onStartImport: (months: WyscoutImportMonths) => Promise<unknown>;
  squadAction?: React.ReactNode;
  clubRosterAction?: React.ReactNode;
  selectedMatchId?: number | null;
  onOpenFixture?: (matchId: number, trigger: HTMLButtonElement) => void;
}

const metric = (value: number | string | null): string => value === null ? 'Not available' : String(value);
const decimal = (value: number | string | null): string => value === null ? 'Not available' : Number(value).toFixed(2);
const average = (value: number | string | null, appearances: number | null): string => (
  value === null || !appearances ? 'Not available' : (Number(value) / appearances).toFixed(2)
);

const ActiveStatus: React.FC<{
  run: WyscoutImportRun;
  isFetching: boolean;
  isQueueActionPending: boolean;
  canQueueAction: boolean;
  dataUpdatedAt: number;
  onRefresh: () => void;
  onPause: () => void;
  onResume: () => void;
}> = ({run, isFetching, isQueueActionPending, canQueueAction, dataUpdatedAt, onRefresh, onPause, onResume}) => {
  const processed = run.succeededCount + run.failedCount;
  const active = run.status === 'queued' || run.status === 'running';
  const paused = run.status === 'paused';
  const percentage = run.selectedCount > 0
    ? Math.max(0, Math.min(100, Math.round((processed / run.selectedCount) * 100)))
    : 0;
  const text = run.status === 'queued'
    ? 'Preparing match history...'
    : run.status === 'running'
      ? `Importing ${processed} of ${run.selectedCount} matches`
      : run.status === 'paused'
        ? 'Import paused'
        : run.status === 'completed'
          ? 'Import complete'
          : run.status === 'partial'
            ? 'Import partially complete'
            : run.retainedData
              ? 'Latest refresh failed'
              : 'Import failed';
  return (
    <div className="space-y-3">
      {active
        ? <LoadingStatus
          label={text}
          description={run.status === 'queued' ? 'WyScout is finding eligible matches. The total will appear when preparation finishes.' : undefined}
          className="font-semibold"
        />
        : <div role="status" aria-live="polite"><p className="font-semibold text-gray-900 dark:text-gray-100">{text}</p></div>}
      {run.status === 'running' && run.selectedCount > 0 && <div
        role="progressbar"
        aria-label={`${processed} of ${run.selectedCount} matches processed`}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percentage}
        className="h-2 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700"
      ><div className="h-full rounded-full bg-club-blue-600 transition-[width] motion-reduce:transition-none" style={{width: `${percentage}%`}} /></div>}
      {active && <p className="text-sm text-gray-600 dark:text-gray-300">Imports run in background batches and may take a while. The import continues if you leave this page.</p>}
      {paused && <p className="text-sm text-gray-600 dark:text-gray-300">This import is paused. Resume it when you are ready for the background queue to continue.</p>}
      {(active || paused) && <div className="flex flex-wrap items-center gap-3">
        <button type="button" disabled={isFetching} onClick={onRefresh} className="min-h-11 rounded-md border border-gray-300 px-3 font-semibold text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200">{isFetching ? 'Refreshing...' : 'Refresh status'}</button>
        {canQueueAction && active && <button type="button" disabled={isQueueActionPending} onClick={onPause} className="min-h-11 rounded-md border border-amber-300 px-3 font-semibold text-amber-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-amber-800 dark:text-amber-200">{isQueueActionPending ? 'Pausing...' : 'Pause import'}</button>}
        {canQueueAction && paused && <button type="button" disabled={isQueueActionPending} onClick={onResume} className="min-h-11 rounded-md bg-club-blue-600 px-4 font-semibold text-white hover:bg-club-blue-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 disabled:cursor-not-allowed disabled:opacity-60">{isQueueActionPending ? 'Resuming...' : 'Resume import'}</button>}
        {dataUpdatedAt > 0 && <p className="text-xs text-gray-500 dark:text-gray-400">Last checked {new Date(dataUpdatedAt).toLocaleTimeString('en-GB')}</p>}
      </div>}
    </div>
  );
};

const Aggregates: React.FC<{aggregates: WyscoutImportAggregates}> = ({aggregates}) => {
  const values = [
    ['Appearances', metric(aggregates.appearances)],
    ['Minutes', metric(aggregates.minutesPlayed)],
    ['Goals', metric(aggregates.goals)],
    ['Assists', metric(aggregates.assists)],
    ['Expected goals', decimal(aggregates.expectedGoals)],
    ['Expected assists', decimal(aggregates.expectedAssists)],
    ['Goals / match', average(aggregates.goals, aggregates.appearances)],
    ['Assists / match', average(aggregates.assists, aggregates.appearances)],
    ['xG / match', average(aggregates.expectedGoals, aggregates.appearances)],
    ['xA / match', average(aggregates.expectedAssists, aggregates.appearances)],
  ];
  return (
    <dl className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
      {values.map(([label, value]) => <div key={label} className="min-w-0"><dt className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{label}</dt><dd className="break-words text-lg font-semibold text-gray-900 dark:text-gray-100">{value}</dd></div>)}
    </dl>
  );
};

const ImportFailures: React.FC<{
  workspaceId: string;
  playerId: string;
  failures: WyscoutImportFailure[];
  canMutate: boolean;
}> = ({workspaceId, playerId, failures, canMutate}) => {
  const queryClient = useQueryClient();
  const panelId = useId();
  const currentRef = React.useRef({workspaceId, playerId, canMutate});
  const pendingTokensRef = React.useRef<Map<number, object>>(new Map());
  currentRef.current = {workspaceId, playerId, canMutate};
  const [expanded, setExpanded] = useState(false);
  const [errors, setErrors] = useState<Record<number, string>>({});
  const [pendingMatchIds, setPendingMatchIds] = useState<Set<number>>(() => new Set());
  const [announcement, setAnnouncement] = useState('');
  const exclusion = useMutation({
    mutationFn: ({matchId, ignored, requestWorkspaceId, requestPlayerId, token}: {
      matchId: number;
      ignored: boolean;
      requestWorkspaceId: string;
      requestPlayerId: string;
      token: object;
    }) => {
      const current = currentRef.current;
      if (!current.canMutate
        || current.workspaceId !== requestWorkspaceId
        || current.playerId !== requestPlayerId
        || pendingTokensRef.current.get(matchId) !== token) return Promise.reject(new Error('Import changes are unavailable'));
      return ignored
        ? wyscoutImportAPI.includeMatch(requestWorkspaceId, requestPlayerId, matchId)
        : wyscoutImportAPI.ignoreMatch(requestWorkspaceId, requestPlayerId, matchId);
    },
    onMutate: ({matchId}) => {
      setPendingMatchIds(current => new Set(current).add(matchId));
      setAnnouncement('');
      setErrors(current => ({...current, [matchId]: ''}));
    },
    onSuccess: async (_data, {matchId, ignored, requestWorkspaceId, requestPlayerId}) => {
      const current = currentRef.current;
      if (current.workspaceId === requestWorkspaceId && current.playerId === requestPlayerId && current.canMutate) {
        setAnnouncement(ignored
          ? `Match ${matchId} will be included in future imports.`
          : `Match ${matchId} will be ignored on future imports.`);
      }
      await queryClient.invalidateQueries({queryKey: playerKeys.wyscoutImport(requestWorkspaceId, requestPlayerId)});
    },
    onError: (error, {matchId, requestWorkspaceId, requestPlayerId}) => {
      const current = currentRef.current;
      if (current.workspaceId === requestWorkspaceId && current.playerId === requestPlayerId && current.canMutate) {
        setErrors(errors => ({...errors, [matchId]: getV2Error(error).message}));
      }
    },
    onSettled: (_data, _error, {matchId, requestWorkspaceId, requestPlayerId, token}) => {
      if (pendingTokensRef.current.get(matchId) === token) pendingTokensRef.current.delete(matchId);
      const current = currentRef.current;
      if (current.workspaceId === requestWorkspaceId && current.playerId === requestPlayerId) {
        setPendingMatchIds(pending => {
          const next = new Set(pending);
          next.delete(matchId);
          return next;
        });
      }
    },
  });
  const resetExclusionRef = React.useRef(exclusion.reset);
  resetExclusionRef.current = exclusion.reset;

  useEffect(() => {
    pendingTokensRef.current.clear();
    setPendingMatchIds(new Set());
    setErrors({});
    setAnnouncement('');
    resetExclusionRef.current();
  }, [canMutate, playerId, workspaceId]);

  const mutateFailure = (matchId: number, ignored: boolean) => {
    const current = currentRef.current;
    if (!current.canMutate || pendingTokensRef.current.has(matchId)) return;
    const token = {};
    pendingTokensRef.current.set(matchId, token);
    exclusion.mutate({
      matchId,
      ignored,
      requestWorkspaceId: current.workspaceId,
      requestPlayerId: current.playerId,
      token,
    });
  };
  const heading = `${failures.length} ${failures.length === 1 ? 'match needs' : 'matches need'} attention`;

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/70 dark:border-amber-900 dark:bg-amber-950/30">
      <button
        type="button"
        aria-expanded={expanded}
        aria-controls={panelId}
        onClick={() => setExpanded(current => !current)}
        className="flex min-h-11 w-full items-center justify-between gap-3 px-4 py-3 text-left font-semibold text-amber-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-club-blue-500 dark:text-amber-100"
      >
        <span>{heading}</span>
        <span aria-hidden="true" className="text-lg">{expanded ? '\u2212' : '+'}</span>
      </button>
      {expanded && <div id={panelId} className="border-t border-amber-200 p-3 dark:border-amber-900 sm:p-4">
        <ul className="space-y-3">
          {failures.map(failure => {
            const isActive = pendingMatchIds.has(failure.wyscoutMatchId);
            const actionLabel = failure.ignored ? 'Include again' : 'Ignore on future imports';
            const pendingLabel = failure.ignored ? 'Including...' : 'Ignoring...';
            return <li key={failure.wyscoutMatchId} className="rounded-lg bg-white p-4 shadow-sm ring-1 ring-amber-200 dark:bg-gray-900 dark:ring-amber-900">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="min-w-0 space-y-1">
                  <p className="font-semibold text-gray-900 dark:text-gray-100">WyScout match {failure.wyscoutMatchId}</p>
                  <p className="text-sm text-gray-700 dark:text-gray-200">{failure.message}</p>
                  <p className="text-sm font-medium text-gray-600 dark:text-gray-300">{failure.retryable ? 'Retry may help' : 'Retry will not help'}</p>
                  {failure.ignored && <p className="text-sm font-semibold text-amber-800 dark:text-amber-200">Ignored on future imports</p>}
                </div>
                {canMutate && <button
                  type="button"
                  disabled={isActive}
                  aria-label={isActive
                    ? `${failure.ignored ? 'Including' : 'Ignoring'} match ${failure.wyscoutMatchId}`
                    : failure.ignored
                      ? `Include match ${failure.wyscoutMatchId} again`
                      : `Ignore match ${failure.wyscoutMatchId} on future imports`}
                  onClick={() => {
                    mutateFailure(failure.wyscoutMatchId, failure.ignored);
                  }}
                  className="min-h-11 shrink-0 rounded-md border border-gray-300 px-3 font-semibold text-gray-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-700 dark:text-gray-200"
                >{isActive ? pendingLabel : actionLabel}</button>}
              </div>
              {errors[failure.wyscoutMatchId] && <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-300">{errors[failure.wyscoutMatchId]}</p>}
            </li>;
          })}
        </ul>
        {announcement && <p aria-live="polite" className="mt-3 text-sm text-gray-700 dark:text-gray-200">{announcement}</p>}
      </div>}
    </div>
  );
};

const WyscoutImportPanel: React.FC<Props> = ({workspaceId, playerId, playerName, position, canImport, onStartImport, squadAction, clubRosterAction, selectedMatchId, onOpenFixture}) => {
  const queryClient = useQueryClient();
  const canStartImport = canImport;
  const currentRef = React.useRef({workspaceId, playerId, canImport, canStartImport, onStartImport});
  const startPendingRef = React.useRef<object | null>(null);
  const retryPendingRef = React.useRef<object | null>(null);
  currentRef.current = {workspaceId, playerId, canImport, canStartImport, onStartImport};
  const [actionError, setActionError] = useState('');
  const [isManualRefresh, setIsManualRefresh] = useState(false);
  const [periodDialog, setPeriodDialog] = useState<'import' | 'reimport' | null>(null);
  const [importInfoOpen, setImportInfoOpen] = useState(false);
  const [months, setMonths] = useState<WyscoutImportMonths>(12);
  const query = useQuery({
    queryKey: [...playerKeys.wyscoutImport(workspaceId, playerId), 'page', 1],
    queryFn: () => wyscoutImportAPI.latest(workspaceId, playerId, 1),
    enabled: Boolean(workspaceId && playerId),
    retry: false,
    refetchInterval: current => current.state.data && (current.state.data.status === 'queued' || current.state.data.status === 'running') ? 5000 : false,
  });
  const currentRunRef = React.useRef<string | null>(query.data?.id ?? null);
  currentRunRef.current = query.data?.id ?? null;
  const action = useMutation({
    mutationFn: ({runId, requestWorkspaceId, requestPlayerId, token}: {
      runId: string;
      requestWorkspaceId: string;
      requestPlayerId: string;
      token: object;
    }) => {
      const current = currentRef.current;
      if (!current.canImport
        || current.workspaceId !== requestWorkspaceId
        || current.playerId !== requestPlayerId
        || currentRunRef.current !== runId
        || retryPendingRef.current !== token) return Promise.reject(new Error('Retry is unavailable'));
      return wyscoutImportAPI.retry(requestWorkspaceId, runId);
    },
    onMutate: () => setActionError(''),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({queryKey: playerKeys.wyscoutImport(variables.requestWorkspaceId, variables.requestPlayerId)}),
        queryClient.invalidateQueries({queryKey: playerKeys.detail(variables.requestWorkspaceId, variables.requestPlayerId), exact: true}),
      ]);
    },
    onError: (error, variables) => {
      const current = currentRef.current;
      if (current.canImport
        && current.workspaceId === variables.requestWorkspaceId
        && current.playerId === variables.requestPlayerId
        && currentRunRef.current === variables.runId) setActionError(getV2Error(error).message);
    },
    onSettled: (_data, _error, variables) => {
      if (retryPendingRef.current === variables.token) retryPendingRef.current = null;
    },
  });
  const queueAction = useMutation({
    mutationFn: ({kind, runId, requestWorkspaceId, requestPlayerId}: {
      kind: 'pause' | 'resume';
      runId: string;
      requestWorkspaceId: string;
      requestPlayerId: string;
    }) => {
      const current = currentRef.current;
      if (!current.canImport
        || current.workspaceId !== requestWorkspaceId
        || current.playerId !== requestPlayerId
        || currentRunRef.current !== runId) return Promise.reject(new Error('Import queue action is unavailable'));
      return kind === 'pause'
        ? wyscoutImportAPI.pause(requestWorkspaceId, runId)
        : wyscoutImportAPI.resume(requestWorkspaceId, runId);
    },
    onMutate: () => setActionError(''),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({queryKey: playerKeys.wyscoutImport(variables.requestWorkspaceId, variables.requestPlayerId)});
    },
    onError: error => setActionError(getV2Error(error).message),
  });
  const resetActionRef = React.useRef(action.reset);
  resetActionRef.current = action.reset;
  const resetQueueActionRef = React.useRef(queueAction.reset);
  resetQueueActionRef.current = queueAction.reset;
  const startImport = useMutation({
    mutationFn: ({selectedMonths, requestWorkspaceId, requestPlayerId}: {
      selectedMonths: WyscoutImportMonths;
      requestWorkspaceId: string;
      requestPlayerId: string;
      token: object;
    }) => {
      const current = currentRef.current;
      if (!current.canStartImport
        || current.workspaceId !== requestWorkspaceId
        || current.playerId !== requestPlayerId) return Promise.reject(new Error('Import is unavailable'));
      return current.onStartImport(selectedMonths);
    },
    onMutate: () => setActionError(''),
    onSuccess: (_data, variables) => {
      const current = currentRef.current;
      if (current.workspaceId === variables.requestWorkspaceId && current.playerId === variables.requestPlayerId) {
        setPeriodDialog(null);
      }
    },
    onError: (error, variables) => {
      const current = currentRef.current;
      if (current.canStartImport
        && current.workspaceId === variables.requestWorkspaceId
        && current.playerId === variables.requestPlayerId
        && startPendingRef.current === variables.token) setActionError(getV2Error(error).message);
    },
    onSettled: (_data, _error, variables) => {
      if (startPendingRef.current === variables.token) startPendingRef.current = null;
    },
  });
  const resetStartImportRef = React.useRef(startImport.reset);
  resetStartImportRef.current = startImport.reset;

  useEffect(() => {
    setPeriodDialog(null);
    setImportInfoOpen(false);
    setMonths(12);
    setActionError('');
    startPendingRef.current = null;
    retryPendingRef.current = null;
    resetActionRef.current();
    resetQueueActionRef.current();
    resetStartImportRef.current();
  }, [playerId, workspaceId]);

  useEffect(() => {
    if (canStartImport) return;
    setPeriodDialog(null);
    setImportInfoOpen(false);
    startPendingRef.current = null;
    retryPendingRef.current = null;
    setActionError('');
    resetActionRef.current();
    resetQueueActionRef.current();
    resetStartImportRef.current();
  }, [canStartImport]);

  const choosePeriod = (kind: 'import' | 'reimport') => {
    if (!currentRef.current.canStartImport || startPendingRef.current) return;
    setMonths(12);
    setActionError('');
    setPeriodDialog(kind);
  };

  const retryRun = (run: WyscoutImportRun) => {
    const current = currentRef.current;
    if (!current.canImport || !run.canRetry || currentRunRef.current !== run.id || retryPendingRef.current) return;
    const token = {};
    retryPendingRef.current = token;
    action.mutate({
      runId: run.id,
      requestWorkspaceId: current.workspaceId,
      requestPlayerId: current.playerId,
      token,
    });
  };

  const mutateQueueState = (run: WyscoutImportRun, kind: 'pause' | 'resume') => {
    const current = currentRef.current;
    if (!current.canImport || currentRunRef.current !== run.id || queueAction.isPending) return;
    queueAction.mutate({kind, runId: run.id, requestWorkspaceId: current.workspaceId, requestPlayerId: current.playerId});
  };

  const notFound = !query.data && (query.error as {code?: string} | null)?.code === 'not_found';
  let importStatus: React.ReactNode = null;
  let importDetails: React.ReactNode = null;
  let matchLog: React.ReactNode = <p className="text-sm text-gray-600 dark:text-gray-300">No imported match log is available.</p>;

  if (query.isPending) {
    importStatus = <LoadingStatus label="Loading WyScout import status..." />;
    matchLog = <LoadingStatus label="Loading imported match log..." />;
  } else if (query.isError && !query.data && !notFound) {
    importStatus = <><p role="alert" className="text-sm text-red-700 dark:text-red-300">Unable to load WyScout import status.</p><button type="button" className="mt-3 min-h-11 rounded-md border border-gray-300 px-3 font-semibold dark:border-gray-700 dark:text-gray-100" onClick={() => { void query.refetch(); }}>Retry status</button></>;
    matchLog = <p className="text-sm text-gray-600 dark:text-gray-300">Imported match log is temporarily unavailable.</p>;
  } else if (notFound) {
    if (canStartImport) {
      importStatus = <>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">No match history has been imported for this player yet.</p>
        <Button className="mt-3 min-h-11" onClick={() => choosePeriod('import')}>Import match statistics</Button>
      </>;
    }
  } else if (query.data) {
    const run = query.data;
    const hasResults = run.matches.length > 0 || Number(run.aggregates?.appearances ?? 0) > 0;
    const canReimport = canStartImport && run.status === 'completed';
    const canRetry = canImport && run.canRetry && (run.status === 'partial' || run.status === 'failed');
    const canAct = canReimport || canRetry;
    const canShowMatchLog = run.status === 'completed' || run.status === 'partial' || (run.status === 'failed' && run.retainedData);
    const actionLabel = run.status === 'completed' ? 'Reimport match statistics' : 'Retry failed matches';
    const pendingLabel = 'Retrying failed matches';
    importStatus = <>
      <ActiveStatus run={run} isFetching={query.isFetching || isManualRefresh} isQueueActionPending={queueAction.isPending} canQueueAction={canStartImport} dataUpdatedAt={query.dataUpdatedAt} onRefresh={() => {
        setIsManualRefresh(true);
        void query.refetch().finally(() => setIsManualRefresh(false));
      }} onPause={() => mutateQueueState(run, 'pause')} onResume={() => mutateQueueState(run, 'resume')} />
      {query.isError && <p role="alert" className="text-sm text-amber-800 dark:text-amber-200">Unable to refresh import status. Showing the last known progress.</p>}
      {(run.status === 'partial' || run.status === 'failed') && run.failures.length === 0 && <p role="alert" className="mt-2 text-sm text-amber-800 dark:text-amber-200">{run.failureSummary || 'Some match data could not be imported.'}</p>}
      {run.status === 'failed' && run.retainedData && <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Showing statistics retained from the last successful import.</p>}
      {run.status === 'partial' && <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">{run.succeededCount} of {run.selectedCount} matches imported{run.failures.length > 0 ? `. ${run.failures.length} ${run.failures.length === 1 ? 'match needs' : 'matches need'} attention.` : ''}</p>}
      {canAct && <button type="button" className="mt-3 min-h-11 rounded-md bg-club-blue-600 px-4 font-semibold text-white hover:bg-club-blue-700 disabled:cursor-not-allowed disabled:opacity-60" disabled={action.isPending || startImport.isPending} onClick={() => run.status === 'completed' ? choosePeriod('reimport') : retryRun(run)} aria-label={action.isPending ? pendingLabel : actionLabel}>{action.isPending ? `${pendingLabel}...` : actionLabel}</button>}
      {actionError && <p role="alert" className="mt-2 text-sm text-red-700 dark:text-red-300">{actionError}</p>}
    </>;
    const hasImportDetails = run.failures.length > 0
      || ((run.status === 'completed' || run.status === 'partial') && !hasResults)
      || Boolean(run.aggregates);
    if (hasImportDetails) {
      importDetails = <section aria-label="WyScout import details" className="min-w-0 rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        {run.failures.length > 0 && <ImportFailures key={`${workspaceId}:${playerId}`} workspaceId={workspaceId} playerId={playerId} failures={run.failures} canMutate={canImport} />}
        {(run.status === 'completed' || run.status === 'partial') && !hasResults && <p className="text-sm text-gray-600 dark:text-gray-300">{run.succeededCount === 0 ? 'No eligible appearances in this period.' : 'Match statistics are not available.'}</p>}
        {run.aggregates && <div className={run.failures.length > 0 ? 'mt-5' : ''}><Aggregates aggregates={run.aggregates} /></div>}
      </section>;
    }
    if (canShowMatchLog) {
      matchLog = <WyscoutStatsSection key={run.id} playerId={playerId} wyscoutPlayerId={run.wyscoutPlayerId} showEventAnalysis={false} selectedMatchId={selectedMatchId} onOpenFixture={onOpenFixture} />;
    }
  }

  const importInfo = <div className="space-y-4">
    <section>
      <h3 className="font-semibold text-gray-900 dark:text-gray-100">What it does</h3>
      <p className="mt-1">Imports the selected 12, 24, or 36 months of match history for this player. The imported data supplies aggregate statistics, analytics, and the match log.</p>
    </section>
    <section>
      <h3 className="font-semibold text-gray-900 dark:text-gray-100">When to use it</h3>
      <p className="mt-1">Use it for the player's first statistics import or when you need to refresh this player's performance data.</p>
    </section>
    <section>
      <h3 className="font-semibold text-gray-900 dark:text-gray-100">What it does not do</h3>
      <p className="mt-1">It does not synchronize the wider squad or change squad lifecycle state. It does not overwrite reports, notes, documents, assessments, medical records, or other staff-authored data.</p>
    </section>
  </div>;
  const managementActionCount = Number(Boolean(squadAction)) + Number(Boolean(clubRosterAction)) + Number(Boolean(importStatus));
  const managementColumns = managementActionCount === 3 ? 'lg:grid-cols-3' : managementActionCount === 2 ? 'lg:grid-cols-2' : '';

  return <div className="min-w-0 space-y-5">
    <section aria-labelledby="wyscout-player-data-heading" className="min-w-0 rounded-xl bg-white p-4 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800 sm:p-5">
      <h2 id="wyscout-player-data-heading" className="text-lg font-bold text-gray-900 dark:text-gray-100">WyScout data</h2>
      <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Manage squad records, discover club roster profiles, and import match statistics from WyScout.</p>
      {managementActionCount > 0 && <div data-testid="wyscout-management-actions" className={`mt-5 grid grid-cols-1 gap-4 ${managementColumns}`}>
        {squadAction}
        {clubRosterAction}
        {importStatus && <article aria-labelledby="wyscout-statistics-heading" className="min-w-0 rounded-lg border border-gray-200 p-4 dark:border-gray-700">
          <div className="flex items-center justify-between gap-3">
            <h3 id="wyscout-statistics-heading" className="font-semibold text-gray-900 dark:text-gray-100">Player match statistics</h3>
            {canStartImport && <button type="button" aria-label="About importing match statistics" onClick={() => setImportInfoOpen(true)} className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-gray-600 hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:text-gray-300 dark:hover:bg-gray-800">
              <InformationCircleIcon aria-hidden="true" className="h-6 w-6" />
            </button>}
          </div>
          <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">This player only</p>
          <div className="mt-3">{importStatus}</div>
        </article>}
      </div>}
    </section>
    {importDetails}
    <section aria-label="WyScout player analytics" className="min-w-0 rounded-xl bg-white p-5 shadow-sm ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
      <WyscoutAnalyticsDashboard workspaceId={workspaceId} playerId={playerId} playerName={playerName} position={position} matchLog={matchLog} />
    </section>
    <ConfirmModal
      isOpen={importInfoOpen}
      title="About importing match statistics"
      message={importInfo}
      confirmLabel="Close"
      hideCancel
      onClose={() => setImportInfoOpen(false)}
      onConfirm={() => setImportInfoOpen(false)}
    />
    <ConfirmModal
      isOpen={periodDialog !== null}
      title={periodDialog === 'reimport' ? 'Reimport match statistics' : 'Import match statistics'}
      message={<div className="space-y-3">
        <p>Choose how much WyScout match history to import for {playerName}.</p>
        <label className="block text-left font-semibold text-gray-800 dark:text-gray-200">
          History to import
          <select value={months} disabled={startImport.isPending} onChange={event => setMonths(Number(event.target.value) as WyscoutImportMonths)} className="mt-1 block min-h-11 w-full rounded-md border border-gray-300 bg-white px-3 text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-club-blue-500 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100">
            <option value={12}>12 months</option>
            <option value={24}>24 months</option>
            <option value={36}>36 months</option>
          </select>
        </label>
        {actionError && <p role="alert" className="text-danger-700 dark:text-danger-300">{actionError}</p>}
      </div>}
      confirmLabel={periodDialog === 'reimport' ? 'Start reimport' : 'Start import'}
      loading={startImport.isPending}
      onClose={() => { if (!startImport.isPending) setPeriodDialog(null); }}
      onConfirm={() => {
        const current = currentRef.current;
        if (!current.canStartImport || startPendingRef.current) return;
        const token = {};
        startPendingRef.current = token;
        startImport.mutate({
          selectedMonths: months,
          requestWorkspaceId: current.workspaceId,
          requestPlayerId: current.playerId,
          token,
        });
      }}
    />
  </div>;
};

export default WyscoutImportPanel;
